const KLAVIYO_OPEN_FORM_RETRY_MS = 250;
const KLAVIYO_OPEN_FORM_MAX_WAIT_MS = 10000;
const KLAVIYO_ONSITE_STORAGE_KEY = 'klaviyoOnsite';
const SECONDS_PER_DAY = 86400;

/**
 * Reads the unix timestamp (seconds) a given Klaviyo form was last closed by
 * this visitor, from Klaviyo's own onsite localStorage record. Checks every
 * form type (modal, flyout, etc.) since the record is keyed by type.
 * @param {string} popupId - Klaviyo popup/form ID.
 * @returns {?number}
 */
function getKlaviyoCloseTimestamp(popupId) {
  let store;

  try {
    store = JSON.parse(localStorage.getItem(KLAVIYO_ONSITE_STORAGE_KEY));
  } catch (e) {
    return null;
  }

  const formTypes = store?.viewedForms ? Object.values(store.viewedForms) : [];

  for (const formType of formTypes) {
    const closedAt = formType?.disabledForms?.[popupId]?.lastCloseTime;
    if (closedAt) return closedAt;
  }

  return null;
}

/**
 * Whether a visitor closed this popup in Klaviyo recently enough that this
 * trigger should be skipped, so it doesn't reopen the popup they just dismissed.
 * @param {string} popupId - Klaviyo popup/form ID.
 * @param {number} dismissDays - Days to respect the close for. 0 respects it indefinitely.
 * @returns {boolean}
 */
function wasRecentlyClosedInKlaviyo(popupId, dismissDays) {
  const closedAt = getKlaviyoCloseTimestamp(popupId);
  if (!closedAt) return false;
  if (dismissDays <= 0) return true;

  const elapsedDays = (Date.now() / 1000 - closedAt) / SECONDS_PER_DAY;
  return elapsedDays < dismissDays;
}

/**
 * Calls klaviyo.openForm once Klaviyo's onsite script is ready, retrying for
 * a short window since the embed script can still be loading when a trigger fires.
 * @param {string} popupId - Klaviyo popup/form ID.
 */
function openKlaviyoForm(popupId) {
  const start = Date.now();

  const attempt = () => {
    if (window.klaviyo && typeof window.klaviyo.openForm === 'function') {
      window.klaviyo.openForm(popupId);
    } else if (Date.now() - start < KLAVIYO_OPEN_FORM_MAX_WAIT_MS) {
      setTimeout(attempt, KLAVIYO_OPEN_FORM_RETRY_MS);
    }
  };

  attempt();
}

if (!customElements.get('klaviyo-popups')) {
  class KlaviyoPopups extends HTMLElement {
    connectedCallback() {
      if (window.Shopify?.designMode) return;

      this.querySelectorAll('.klaviyo-popup-trigger').forEach((trigger) => this.initTrigger(trigger));
    }

    /**
     * Wires up a single block's trigger, once its device target matches the
     * current viewport.
     * @param {HTMLElement} trigger - Trigger element carrying the block's data-* config.
     */
    initTrigger(trigger) {
      const { popupId, enabled, device, trigger: triggerType, dismissDays } = trigger.dataset;

      if (enabled !== 'true' || !popupId) return;

      const isDesktop = theme.mediaMatches.md;
      if (device === 'desktop' && !isDesktop) return;
      if (device === 'mobile' && isDesktop) return;
      if (wasRecentlyClosedInKlaviyo(popupId, Number(dismissDays))) return;

      let fired = false;
      let cleanup = () => {};
      const fire = () => {
        if (fired) return;
        fired = true;
        openKlaviyoForm(popupId);
        cleanup();
      };

      switch (triggerType) {
        case 'immediate':
          fire();
          break;

        case 'interaction': {
          const events = ['click', 'touchstart', 'keydown', 'scroll'];
          events.forEach((evt) => document.addEventListener(evt, fire, { passive: true, once: true }));
          cleanup = () => events.forEach((evt) => document.removeEventListener(evt, fire));
          break;
        }

        case 'timer': {
          const seconds = Number(trigger.dataset.timerSeconds) || 5;
          const timeout = setTimeout(fire, seconds * 1000);
          cleanup = () => clearTimeout(timeout);
          break;
        }

        case 'scroll': {
          const threshold = Number(trigger.dataset.scrollPercentage) || 50;
          const handler = () => {
            const scrollable = document.documentElement.scrollHeight - window.innerHeight;
            const scrolled = scrollable > 0 ? (window.scrollY / scrollable) * 100 : 100;
            if (scrolled >= threshold) fire();
          };
          document.addEventListener('scroll', handler, { passive: true });
          cleanup = () => document.removeEventListener('scroll', handler);
          handler();
          break;
        }

        case 'exit_intent':
          if (isDesktop) {
            const handler = (evt) => {
              if (evt.clientY < 0) fire();
            };
            document.body.addEventListener('mouseleave', handler);
            cleanup = () => document.body.removeEventListener('mouseleave', handler);
          } else {
            const seconds = Number(trigger.dataset.idleSeconds) || 30;
            const idleEvents = ['mousemove', 'touchstart', 'scroll', 'keydown'];
            let idleTimeout;
            const resetIdle = () => {
              clearTimeout(idleTimeout);
              idleTimeout = setTimeout(fire, seconds * 1000);
            };
            idleEvents.forEach((evt) => document.addEventListener(evt, resetIdle, { passive: true }));
            resetIdle();
            cleanup = () => {
              clearTimeout(idleTimeout);
              idleEvents.forEach((evt) => document.removeEventListener(evt, resetIdle));
            };
          }
          break;

        default:
          break;
      }
    }
  }

  customElements.define('klaviyo-popups', KlaviyoPopups);
}
